import { safeStorage } from './safeStorage'

// ── Pending-invite carry-through ──────────────────────────────────────────────
// The invite loop was silently broken: attribution only fired if a LOGGED-IN user
// re-visited /invite/:code, but a brand-new recipient goes invite → login → lands
// on Home and never returns to the invite route, so `invited_by_id` was never set
// and the inviter got no credit — the exact word-of-mouth conversion that matters
// most. This module persists the code across the signup + magic-link round-trip
// (same-browser via storage, cross-device via the ?invite= redirect param) and
// replays it against invite-accept once a session exists.
const KEY = 'nextep_pending_invite'
// A stashed code is only valid for a short window: a user who opens an invite link
// but abandons signup, then signs in normally days later, must NOT retro-credit the
// inviter for that unrelated login. Mirrors the bridge-cookie's freshness discipline.
const TTL_MS = 60 * 60 * 1000  // 1 hour

// Read a pending code from the URL (?invite=CODE, survives cross-device magic links)
// or from storage (survives the same-browser signup hop, with a freshness TTL).
// Uppercased to match the server's code.toUpperCase() validation.
export function readPendingInvite() {
  try {
    const u = new URLSearchParams(window.location.search).get('invite')
    if (u) return String(u).toUpperCase()   // fresh from a redirect, no TTL needed
  } catch { /* no window/search */ }
  const raw = safeStorage.get(KEY)
  if (!raw) return null
  try {
    const { c, t } = JSON.parse(raw)
    if (!c || (t && Date.now() - t > TTL_MS)) { safeStorage.remove(KEY); return null }
    return String(c).toUpperCase()
  } catch {
    // Legacy plain-string value (pre-TTL) — accept once; the next stash re-writes JSON.
    return String(raw).toUpperCase()
  }
}

export function stashPendingInvite(code) {
  if (!code) return
  try { safeStorage.set(KEY, JSON.stringify({ c: String(code).toUpperCase(), t: Date.now() })) } catch { /* storage blocked */ }
}

export function clearPendingInvite() {
  safeStorage.remove(KEY)
  // Strip ?invite= from the URL so a refresh / re-mount can't re-trigger a redeem.
  try {
    const url = new URL(window.location.href)
    if (url.searchParams.has('invite')) {
      url.searchParams.delete('invite')
      window.history.replaceState(null, '', url.pathname + url.search + url.hash)
    }
  } catch { /* no window */ }
}

// Per-uid guard so the several auth events that fire on boot (INITIAL_SESSION,
// SIGNED_IN, …) don't each re-POST. Reset on sign-out so a different user in the
// same tab starts clean.
const redeemed = new Set()
export function resetRedeemedInvites() { redeemed.clear() }

// Redeem a pending invite for the just-authenticated session. Robustness (M2): the
// stash is only SETTLED (marked done + cleared) on a DEFINITIVE outcome — a 2xx
// success or a 4xx client rejection (invalid/used code — no point retrying). A
// network throw or 5xx is TRANSIENT: we leave the stash intact and unmarked so the
// next auth event (or next boot within the TTL) retries, instead of permanently
// losing attribution to one server hiccup.
export async function redeemPendingInvite(session) {
  const uid   = session?.user?.id
  const token = session?.access_token
  if (!uid || !token || redeemed.has(uid)) return
  const code = readPendingInvite()
  if (!code) return
  try {
    const res = await fetch('/.netlify/functions/invite-accept', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ code }),
    })
    // Definitive (success or client-side reject) → settle. Transient (5xx) → keep for retry.
    if (res.ok || (res.status >= 400 && res.status < 500)) {
      redeemed.add(uid)
      clearPendingInvite()
    }
  } catch {
    // Network error — leave the stash so a later auth event retries. Don't mark redeemed.
  }
}
